/**
 * custom-blend-pricing.js
 * Witbran — Custom Blend Pricing Engine
 *
 * PURE FUNCTIONS ONLY — no DOM, no side-effects.
 * Shared between:
 *   - Browser (loaded before custom-blend-builder.js)
 *   - Jest unit tests (require/import)
 *   - Shopify Function validator (copied into extension)
 *
 * Namespace: WitbranPricing
 */

(function (root, factory) {
  /* UMD wrapper — works in browser, Node.js (Jest), and Shopify Functions */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = factory();
  } else {
    root.WitbranPricing = factory();
  }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /**
   * Calculate the total price for a custom blend order.
   *
   * @param {Object} params
   * @param {Object} params.grains         - { key: { cost_per_kg, ... } }
   * @param {Object} params.selection      - { key: pct } e.g. { jowar: 60, bajra: 40 }
   * @param {number} params.packSizeKg     - 1 or 5
   * @param {number} params.packaging      - flat packaging cost for this pack size (INR)
   * @param {number} params.logistics      - flat logistics cost for this pack size (INR)
   * @param {number} params.marginPct      - margin percentage e.g. 20 (means 20%)
   *
   * @returns {Object} {
   *   blendCost:       number,   // raw grain cost before margin
   *   marginAmount:    number,   // margin in rupees
   *   blendWithMargin: number,   // blendCost × (1 + margin/100)
   *   packaging:       number,
   *   logistics:       number,
   *   total:           number,   // final customer price (rounded up to ₹1)
   *   lineItems:       Array<{ key, name, pct, weightKg, costPerKg, lineTotal }>,
   *   errors:          string[]  // empty if valid
   * }
   */
  function calculatePrice(params) {
    var errors = [];
    var result = {
      blendCost: 0,
      marginAmount: 0,
      blendWithMargin: 0,
      packaging: 0,
      logistics: 0,
      total: 0,
      lineItems: [],
      errors: errors
    };

    // ── Input validation ──────────────────────────────────────────────
    if (!params || typeof params !== 'object') {
      errors.push('Invalid params object');
      return result;
    }

    var grains    = params.grains    || {};
    var selection = params.selection || {};
    var packSize  = Number(params.packSizeKg);
    var packaging = Number(params.packaging);
    var logistics = Number(params.logistics);
    var margin    = Number(params.marginPct);

    if (packSize !== 1 && packSize !== 5) {
      errors.push('Pack size must be 1 or 5 kg');
    }
    if (isNaN(packaging) || packaging < 0) {
      errors.push('Invalid packaging cost');
    }
    if (isNaN(logistics) || logistics < 0) {
      errors.push('Invalid logistics cost');
    }
    if (isNaN(margin) || margin < 0 || margin > 200) {
      errors.push('Margin must be between 0 and 200');
    }

    // ── Proportion validation ─────────────────────────────────────────
    var keys         = Object.keys(selection);
    var numGrains    = keys.length;
    // maxGrains is derived from the grain catalogue — dynamically supports
    // any number of grains; add/remove grains via metafield only.
    var numAvailable = Object.keys(grains).length;
    var maxGrains    = numAvailable > 0 ? numAvailable : 20;

    if (numGrains < 2) {
      errors.push('At least 2 grains must be selected');
    }
    if (numGrains > maxGrains) {
      errors.push('Maximum ' + maxGrains + ' grains allowed');
    }

    var totalPct = 0;
    for (var i = 0; i < keys.length; i++) {
      var k   = keys[i];
      var pct = Number(selection[k]);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        errors.push('Invalid proportion for grain: ' + k);
      }
      if (pct < 5) {
        errors.push('Minimum 5% required per grain (' + k + ')');
      }
      totalPct += pct;
    }

    if (Math.abs(totalPct - 100) > 0.01) {
      errors.push('Proportions must total 100% (got ' + totalPct.toFixed(1) + '%)');
    }

    if (errors.length > 0) {
      return result;
    }

    // ── Price calculation ─────────────────────────────────────────────
    var blendCost = 0;
    var lineItems = [];

    for (var j = 0; j < keys.length; j++) {
      var grainKey  = keys[j];
      var grainPct  = Number(selection[grainKey]);
      var grainData = grains[grainKey];

      if (!grainData) {
        errors.push('Grain not found in price list: ' + grainKey);
        return result;
      }

      var costPerKg  = Number(grainData.cost_per_kg);
      var weightKg   = (grainPct / 100) * packSize;          // e.g. 60% of 5kg = 3kg
      var lineTotal  = round2(costPerKg * weightKg);

      blendCost += lineTotal;

      lineItems.push({
        key:        grainKey,
        name:       grainData.name,
        pct:        grainPct,
        weightKg:   round2(weightKg),
        costPerKg:  costPerKg,
        lineTotal:  lineTotal
      });
    }

    var blendWithMargin = round2(blendCost * (1 + margin / 100));
    var marginAmount    = round2(blendWithMargin - blendCost);
    var rawTotal        = blendWithMargin + packaging + logistics;
    var total           = Math.ceil(rawTotal); // round UP to nearest ₹1

    result.blendCost       = round2(blendCost);
    result.marginAmount    = marginAmount;
    result.blendWithMargin = blendWithMargin;
    result.packaging       = packaging;
    result.logistics       = logistics;
    result.total           = total;
    result.lineItems       = lineItems;

    return result;
  }

  /**
   * Validate a selection object (grains + proportions).
   * Returns { valid: bool, errors: string[] }
   */
  function validateSelection(selection, minGrains, maxGrains, minPct) {
    minGrains = minGrains || 2;
    // maxGrains defaults to 20 — in practice it equals the grain catalogue
    // size, which is passed in by the caller (WitbranBlendConfig.maxGrains).
    maxGrains = maxGrains || 20;
    minPct    = minPct    || 5;

    var errors = [];
    var keys   = Object.keys(selection || {});

    if (keys.length < minGrains) {
      errors.push('Select at least ' + minGrains + ' grains');
    }
    if (keys.length > maxGrains) {
      errors.push('Maximum ' + maxGrains + ' grains allowed');
    }

    var total = 0;
    for (var i = 0; i < keys.length; i++) {
      var pct = Number(selection[keys[i]]);
      if (pct < minPct) {
        errors.push('Minimum ' + minPct + '% per grain');
        break;
      }
      total += pct;
    }

    if (keys.length >= minGrains && Math.abs(total - 100) > 0.01) {
      errors.push('Total must equal 100% (currently ' + total.toFixed(0) + '%)');
    }

    return { valid: errors.length === 0, errors: errors };
  }

  /**
   * Auto-balance proportions equally across selected grains.
   * Handles rounding so total is always exactly 100.
   * e.g. 3 grains → [34, 33, 33]
   *
   * @param {string[]} grainKeys
   * @param {number}   stepPct    — must snap to this step (default 5)
   * @returns {Object} { key: pct }
   */
  function autoBalance(grainKeys, stepPct) {
    stepPct = stepPct || 5;
    var n = grainKeys.length;
    if (n === 0) return {};

    var base      = Math.floor(100 / n / stepPct) * stepPct;
    var remainder = 100 - (base * n);
    var result    = {};

    for (var i = 0; i < n; i++) {
      result[grainKeys[i]] = base;
    }

    // Distribute remainder in stepPct chunks to first grains
    var i = 0;
    while (remainder > 0) {
      result[grainKeys[i % n]] += stepPct;
      remainder -= stepPct;
      i++;
    }

    return result;
  }

  /**
   * Serialize a blend selection into a structured JSON array
   * for storage as a Shopify line item property.
   *
   * @param {Object} selection   { key: pct }
   * @param {Object} grains      { key: { name, ... } }
   * @param {number} packSizeKg
   * @returns {Array} [{ key, name, pct, weightKg }]
   */
  function serializeBlend(selection, grains, packSizeKg) {
    return Object.keys(selection).map(function (key) {
      var pct = Number(selection[key]);
      return {
        key:      key,
        name:     grains[key] ? grains[key].name : key,
        pct:      pct,
        weightKg: round2((pct / 100) * packSizeKg)
      };
    });
  }

  /**
   * Parse a reorder URL param back into a selection object.
   * @param {string} blendJson  — URL-decoded JSON string
   * @returns {{ selection: Object, packSizeKg: number }|null}
   */
  function parseReorderParam(blendJson) {
    try {
      var data = JSON.parse(blendJson);
      if (!Array.isArray(data)) return null;

      var selection = {};
      for (var i = 0; i < data.length; i++) {
        var item = data[i];
        if (item.key && typeof item.pct === 'number') {
          selection[item.key] = item.pct;
        }
      }
      return { selection: selection };
    } catch (e) {
      return null;
    }
  }

  /** Round to 2 decimal places */
  function round2(n) {
    return Math.round(n * 100) / 100;
  }

  // ── Public API ──────────────────────────────────────────────────────
  return {
    calculatePrice:    calculatePrice,
    validateSelection: validateSelection,
    autoBalance:       autoBalance,
    serializeBlend:    serializeBlend,
    parseReorderParam: parseReorderParam,
    round2:            round2
  };
}));
